/* ===========================================================
   Arrow cinematic homepage — behaviour
   Progressive enhancement for motion, reveals, video playback,
   theme-editor section reloads, and native Shopify product forms.
   =========================================================== */
(function () {
  'use strict';

  var runtime = window.__arrowCinematicRuntime;

  if (!runtime) {
    runtime = createRuntime();
    window.__arrowCinematicRuntime = runtime;
    runtime.bindGlobal();
  }

  runtime.initWhenReady(document);

  function createRuntime() {
    var motionStorageKey = 'arrowCinematicMotion';
    var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    var userMotionPreference = readMotionPreference();
    var revealObserver = null;
    var videoObserver = null;
    var revealElements = [];
    var heroControllers = [];
    var readyRoots = [];
    var readyBound = false;
    var globalBound = false;
    var api = {};

    function each(list, callback) {
      Array.prototype.forEach.call(list, callback);
    }

    function matches(element, selector) {
      var matcher = element.matches || element.msMatchesSelector || element.webkitMatchesSelector;
      return matcher ? matcher.call(element, selector) : false;
    }

    function collect(root, selector) {
      var elements = [];
      if (!root) return elements;
      if (root.nodeType === 1 && matches(root, selector)) elements.push(root);
      if (root.querySelectorAll) {
        each(root.querySelectorAll(selector), function (element) {
          elements.push(element);
        });
      }
      return elements;
    }

    function closestArrowRoot(element) {
      while (element && element !== document) {
        if (element.nodeType === 1 && element.classList.contains('arrow-cin')) return element;
        element = element.parentNode;
      }
      return null;
    }

    function safePlay(video) {
      var promise;
      if (!video) return;
      promise = video.play();
      if (promise && promise.catch) promise.catch(function () {});
    }

    function readMotionPreference() {
      try {
        return window.sessionStorage.getItem(motionStorageKey) || '';
      } catch (err) {
        return '';
      }
    }

    function writeMotionPreference(value) {
      try {
        window.sessionStorage.setItem(motionStorageKey, value);
      } catch (err) {}
    }

    function motionIsPaused() {
      if (userMotionPreference === 'pause') return true;
      if (userMotionPreference === 'play') return false;
      return motionQuery.matches;
    }

    function reveal(element) {
      element.classList.add('is-in');
    }

    function revealAll() {
      revealElements = revealElements.filter(function (element) {
        if (!document.documentElement.contains(element)) return false;
        reveal(element);
        if (revealObserver) revealObserver.unobserve(element);
        return true;
      });
    }

    function sweepReveals() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      revealElements = revealElements.filter(function (element) {
        var rect;
        if (!document.documentElement.contains(element)) return false;
        if (element.classList.contains('is-in')) return true;
        if (viewportHeight <= 0) {
          reveal(element);
          return true;
        }
        rect = element.getBoundingClientRect();
        if (rect.top < viewportHeight * 0.95) reveal(element);
        return true;
      });
    }

    function ensureRevealObserver() {
      if (revealObserver || !('IntersectionObserver' in window)) return;
      revealObserver = new IntersectionObserver(function (entries) {
        each(entries, function (entry) {
          if (entry.isIntersecting) {
            reveal(entry.target);
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.14, rootMargin: '0px 0px -5% 0px' });
    }

    function initReveal(root) {
      var elements = collect(root, '.arrow-cin .ac-reveal');

      if (!elements.length) return;
      ensureRevealObserver();

      each(elements, function (element) {
        var wrapper;
        if (element.getAttribute('data-ac-reveal-init') === 'true') return;
        element.setAttribute('data-ac-reveal-init', 'true');
        revealElements.push(element);

        if (motionIsPaused() || !revealObserver) {
          reveal(element);
          return;
        }

        wrapper = closestArrowRoot(element);
        if (wrapper) wrapper.classList.add('ac-js');
        revealObserver.observe(element);
        window.setTimeout(function () {
          if (document.documentElement.contains(element)) reveal(element);
        }, 3000);
      });

      sweepReveals();
    }

    function ensureVideoObserver() {
      if (videoObserver || !('IntersectionObserver' in window)) return;
      videoObserver = new IntersectionObserver(function (entries) {
        each(entries, function (entry) {
          var video = entry.target;
          video.setAttribute('data-ac-in-view', entry.isIntersecting ? 'true' : 'false');
          if (entry.isIntersecting && !motionIsPaused()) {
            safePlay(video);
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.25 });
    }

    function initVideo(root) {
      var videos = collect(root, '.arrow-cin video:not([data-ac-hero-video]):not([data-ac-hero-mobile-video])');

      if (!videos.length) return;
      ensureVideoObserver();

      each(videos, function (video) {
        if (video.getAttribute('data-ac-video-init') === 'true') return;
        video.setAttribute('data-ac-video-init', 'true');
        video.removeAttribute('autoplay');

        if (videoObserver) {
          videoObserver.observe(video);
        } else if (!motionIsPaused()) {
          video.setAttribute('data-ac-in-view', 'true');
          safePlay(video);
        } else {
          video.pause();
        }
      });
    }

    function initHeroReel(root) {
      var heroVideos = collect(root, '.arrow-cin [data-ac-hero-video]');

      each(heroVideos, function (heroVideo) {
        var wrapper;
        var mobileVideo;
        var hero;
        var mobileQuery;
        var reel;
        var index = 0;
        var mobileSource;
        var controller;

        if (heroVideo.getAttribute('data-ac-hero-init') === 'true') return;
        heroVideo.setAttribute('data-ac-hero-init', 'true');

        wrapper = closestArrowRoot(heroVideo);
        mobileVideo = wrapper ? wrapper.querySelector('[data-ac-hero-mobile-video]') : null;
        hero = wrapper ? wrapper.querySelector('.ac-hero') : null;
        mobileQuery = window.matchMedia('(max-width: 760px)');
        mobileSource = mobileVideo ? mobileVideo.getAttribute('data-ac-src') : '';

        try {
          reel = JSON.parse(heroVideo.getAttribute('data-ac-reel') || '[]');
        } catch (err) {
          reel = [];
        }

        function clearSource(video) {
          if (!video) return;
          video.pause();
          if (video.getAttribute('src')) {
            video.removeAttribute('src');
            video.load();
          }
        }

        function playDesktop(currentIndex) {
          if (mobileQuery.matches || !reel.length || motionIsPaused()) return;
          if (heroVideo.getAttribute('src') !== reel[currentIndex]) {
            heroVideo.src = reel[currentIndex];
            heroVideo.load();
          }
          safePlay(heroVideo);
        }

        function activate() {
          var isMobile = mobileQuery.matches;

          if (hero) hero.classList.toggle('ac-hero--mobile-poster', isMobile && !mobileVideo);

          if (motionIsPaused()) {
            heroVideo.pause();
            if (mobileVideo) mobileVideo.pause();
            return;
          }

          if (isMobile) {
            clearSource(heroVideo);
            if (mobileVideo && mobileSource) {
              if (mobileVideo.getAttribute('src') !== mobileSource) {
                mobileVideo.src = mobileSource;
                mobileVideo.load();
              }
              safePlay(mobileVideo);
            }
            return;
          }

          clearSource(mobileVideo);
          playDesktop(index);
        }

        function handleEnded() {
          if (motionIsPaused() || mobileQuery.matches || !reel.length) return;
          index = (index + 1) % reel.length;
          playDesktop(index);
        }

        function pause() {
          heroVideo.pause();
          if (mobileVideo) mobileVideo.pause();
        }

        function destroy() {
          pause();
          heroVideo.removeEventListener('ended', handleEnded);
          if (mobileQuery.removeEventListener) {
            mobileQuery.removeEventListener('change', activate);
          } else if (mobileQuery.removeListener) {
            mobileQuery.removeListener(activate);
          }
          heroVideo.removeAttribute('data-ac-hero-init');
          heroVideo._acHeroController = null;
          heroControllers = heroControllers.filter(function (item) { return item !== controller; });
        }

        controller = {
          element: heroVideo,
          pause: pause,
          resume: activate,
          destroy: destroy
        };

        heroVideo._acHeroController = controller;
        heroControllers.push(controller);
        heroVideo.addEventListener('ended', handleEnded);

        if (mobileQuery.addEventListener) {
          mobileQuery.addEventListener('change', activate);
        } else if (mobileQuery.addListener) {
          mobileQuery.addListener(activate);
        }

        activate();
      });
    }

    function initBuyBox(root) {
      var boxes = collect(root, '.arrow-cin [data-ac-buy]');

      each(boxes, function (box) {
        var errorElement;
        var groups;
        var typeInputs;
        var planHeads;
        var summaryElement;
        var priceElement;
        var addLabels;
        var optionInputs;
        var forms;
        var listeners = [];
        var controller;

        if (box.getAttribute('data-ac-buy-init') === 'true') return;
        box.setAttribute('data-ac-buy-init', 'true');

        errorElement = box.querySelector('[data-ac-error]');
        groups = box.querySelectorAll('[data-ac-group]');
        typeInputs = box.querySelectorAll('[data-ac-type]');
        planHeads = box.querySelectorAll('[data-ac-planhead]');
        summaryElement = box.querySelector('[data-ac-summary]');
        priceElement = box.querySelector('[data-ac-selected-price]');
        addLabels = box.querySelectorAll('[data-ac-add-label]');
        optionInputs = box.querySelectorAll('[data-ac-group] .ac-choice__input');
        forms = box.querySelectorAll('form.ac-buy__form');

        function bind(element, eventName, handler) {
          element.addEventListener(eventName, handler);
          listeners.push({ element: element, eventName: eventName, handler: handler });
        }

        function syncSelectedClass() {
          each(box.querySelectorAll('.ac-choice'), function (choice) {
            var input = choice.querySelector('.ac-choice__input');
            var option = choice.querySelector('.ac-opt');
            if (input && option) option.classList.toggle('is-sel', input.checked);
          });
        }

        function showGroup(type) {
          each(groups, function (group) {
            group.hidden = group.getAttribute('data-ac-group') !== type;
          });
          each(planHeads, function (planHead) {
            planHead.textContent = type === 'subscription' ? 'Schedule' : 'Quantity';
          });
        }

        function updateAddLabels(typeValue) {
          each(addLabels, function (label) {
            var button = label.parentNode;
            if (button && button.disabled) return;
            label.textContent = typeValue === 'subscription'
              ? 'Add subscription to cart'
              : 'Add one-time purchase to cart';
          });
        }

        function updateSelectionSummary() {
          var type = box.querySelector('[data-ac-type]:checked');
          var typeValue = type ? type.value : 'onetime';
          var activeGroup = box.querySelector('[data-ac-group="' + typeValue + '"]');
          var chosen = activeGroup ? activeGroup.querySelector('.ac-choice__input:checked') : null;
          var price;
          var cadence;
          var typeLabel;

          if (!chosen) {
            if (summaryElement) summaryElement.textContent = 'Select a purchase option.';
            return null;
          }

          price = chosen.getAttribute('data-price') || '';
          cadence = chosen.getAttribute('data-cadence') || '';
          typeLabel = typeValue === 'subscription' ? 'Subscribe' : 'One-time';

          if (priceElement && price) priceElement.textContent = price;
          if (summaryElement) {
            summaryElement.textContent = typeLabel + (price ? ' — ' + price : '') + (cadence ? ' · ' + cadence : '');
          }
          updateAddLabels(typeValue);
          return chosen;
        }

        function clearError() {
          if (errorElement) errorElement.textContent = '';
        }

        each(typeInputs, function (input) {
          bind(input, 'change', function () {
            showGroup(input.value);
            syncSelectedClass();
            updateSelectionSummary();
            clearError();
          });
        });

        each(optionInputs, function (input) {
          bind(input, 'change', function () {
            syncSelectedClass();
            updateSelectionSummary();
            clearError();
          });
        });

        each(forms, function (form) {
          bind(form, 'submit', function (event) {
            var addButton;
            var variantInput;
            var quantityInput;
            var sellingPlanInput;
            var payload;
            var original;
            var sellingPlan;
            var cartRoot;

            if (!window.fetch) return;
            event.preventDefault();

            addButton = form.querySelector('[data-ac-add]');
            if (!addButton || addButton.disabled) return;

            variantInput = form.querySelector('[name="id"]');
            quantityInput = form.querySelector('[name="quantity"]');
            sellingPlanInput = form.querySelector('[name="selling_plan"]');

            if (!variantInput || !quantityInput) {
              if (errorElement) errorElement.textContent = 'Select a purchase option before adding to cart.';
              return;
            }

            payload = {
              id: Number(variantInput.value),
              quantity: Number(quantityInput.value || 1)
            };
            sellingPlan = sellingPlanInput ? sellingPlanInput.value : '';
            if (sellingPlan) payload.selling_plan = Number(sellingPlan);

            original = addButton.innerHTML;
            addButton.disabled = true;
            addButton.setAttribute('aria-busy', 'true');
            form.setAttribute('aria-busy', 'true');
            addButton.textContent = 'Adding…';
            clearError();

            cartRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
              ? window.Shopify.routes.root
              : '/';

            window.fetch(cartRoot + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [payload] })
            })
              .then(function (response) {
                return response.json().then(function (data) {
                  if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart.');
                  return data;
                });
              })
              .then(function () {
                window.location.href = cartRoot + 'cart';
              })
              .catch(function (error) {
                addButton.disabled = false;
                addButton.removeAttribute('aria-busy');
                form.removeAttribute('aria-busy');
                addButton.innerHTML = original;
                if (errorElement) errorElement.textContent = error.message || 'Could not add to cart. Please try again.';
              });
          });
        });

        syncSelectedClass();
        showGroup((box.querySelector('[data-ac-type]:checked') || {}).value || 'onetime');
        updateSelectionSummary();

        function destroy() {
          each(listeners, function (listener) {
            listener.element.removeEventListener(listener.eventName, listener.handler);
          });
          box.removeAttribute('data-ac-buy-init');
          box._acBuyController = null;
        }

        controller = { destroy: destroy };
        box._acBuyController = controller;
      });
    }

    function ensureMotionControl() {
      var host;
      var button;

      if (!document.querySelector('.arrow-cin')) return;
      host = document.querySelector('[data-ac-motion-control-host]');

      if (!host) {
        host = document.createElement('div');
        host.className = 'arrow-cin ac-motion-control-host';
        host.setAttribute('data-ac-motion-control-host', 'true');
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'ac-motion-control';
        button.setAttribute('data-ac-motion-toggle', 'true');
        host.appendChild(button);
        document.body.appendChild(host);
      } else {
        button = host.querySelector('[data-ac-motion-toggle]');
      }

      if (button && button.getAttribute('data-ac-motion-init') !== 'true') {
        button.setAttribute('data-ac-motion-init', 'true');
        button.addEventListener('click', function () {
          userMotionPreference = motionIsPaused() ? 'play' : 'pause';
          writeMotionPreference(userMotionPreference);
          applyMotionState();
        });
      }
    }

    function updateMotionControl(paused) {
      var button = document.querySelector('[data-ac-motion-toggle]');
      var label = paused ? 'Play motion' : 'Pause motion';
      if (!button) return;
      button.textContent = label;
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-pressed', paused ? 'true' : 'false');
    }

    function applyMotionState() {
      var paused = motionIsPaused();
      var allowMotion = userMotionPreference === 'play';

      each(document.querySelectorAll('.arrow-cin'), function (wrapper) {
        wrapper.classList.toggle('ac-motion-paused', paused);
        wrapper.classList.toggle('ac-motion-allow', allowMotion);
      });

      if (paused) {
        each(document.querySelectorAll('.arrow-cin video'), function (video) {
          video.pause();
        });
        each(heroControllers.slice(), function (controller) {
          controller.pause();
        });
        revealAll();
      } else {
        each(document.querySelectorAll('.arrow-cin video[data-ac-video-init][data-ac-in-view="true"]'), function (video) {
          safePlay(video);
        });
        each(heroControllers.slice(), function (controller) {
          controller.resume();
        });
      }

      updateMotionControl(paused);
    }

    function destroy(root) {
      each(collect(root, '.arrow-cin [data-ac-buy]'), function (box) {
        if (box._acBuyController) box._acBuyController.destroy();
      });

      each(collect(root, '.arrow-cin [data-ac-hero-video]'), function (heroVideo) {
        if (heroVideo._acHeroController) heroVideo._acHeroController.destroy();
      });

      each(collect(root, '.arrow-cin video[data-ac-video-init]'), function (video) {
        if (videoObserver) videoObserver.unobserve(video);
        video.pause();
        video.removeAttribute('data-ac-video-init');
        video.removeAttribute('data-ac-in-view');
      });

      each(collect(root, '.arrow-cin .ac-reveal[data-ac-reveal-init]'), function (element) {
        if (revealObserver) revealObserver.unobserve(element);
        element.removeAttribute('data-ac-reveal-init');
        revealElements = revealElements.filter(function (item) { return item !== element; });
      });
    }

    function init(root) {
      ensureMotionControl();
      initReveal(root);
      initVideo(root);
      initHeroReel(root);
      initBuyBox(root);
      applyMotionState();
    }

    function flushReadyRoots() {
      var roots = readyRoots.slice();
      readyRoots = [];
      each(roots, function (root) { init(root); });
    }

    function initWhenReady(root) {
      if (document.readyState !== 'loading') {
        init(root);
        return;
      }

      readyRoots.push(root);
      if (readyBound) return;
      readyBound = true;
      document.addEventListener('DOMContentLoaded', flushReadyRoots);
    }

    function bindGlobal() {
      if (globalBound) return;
      globalBound = true;

      window.addEventListener('scroll', sweepReveals, { passive: true });
      window.addEventListener('resize', sweepReveals, { passive: true });
      window.addEventListener('load', sweepReveals);

      document.addEventListener('shopify:section:load', function (event) {
        init(event.target);
      });
      document.addEventListener('shopify:section:unload', function (event) {
        destroy(event.target);
      });

      if (motionQuery.addEventListener) {
        motionQuery.addEventListener('change', applyMotionState);
      } else if (motionQuery.addListener) {
        motionQuery.addListener(applyMotionState);
      }
    }

    api.bindGlobal = bindGlobal;
    api.destroy = destroy;
    api.init = init;
    api.initWhenReady = initWhenReady;
    return api;
  }
})();
